import {
  cn,
  Input as V2Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
  InputGroupTextarea,
  Textarea as V2Textarea,
} from '@foresightdata/web-common-components';
import {
  InputAdornment,
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
} from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  CSSProperties,
  InputHTMLAttributes,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
  Ref,
  useId,
} from 'react';
import { MaggieFeatureFlags } from '../../util/feature-flags';

/**
 * The app's TextField (FD-6105). Takes MUI's props and renders MUI's TextField, or, when the
 * `useV2Components` flag is on, web-common's Input / Textarea (in an InputGroup when there are
 * adornments) with the label above and the helper text below. Call sites switch to this wrapper
 * unchanged; the flag is the only switch.
 *
 * Deliberately the props the app uses and nothing else. MUI's nested `slotProps` / `InputProps`
 * are flattened: `inputProps` is the native input's attributes, `startAdornment` / `endAdornment`
 * are the bare nodes (the wrapper adds MUI's InputAdornment itself). No `sx`: use `className`.
 * `size` and `variant` are accepted for v1; v2 renders every field at the theme's (small,
 * outlined) size.
 */
export interface TextFieldProps extends Pick<
  MuiTextFieldProps,
  | 'value'
  | 'defaultValue'
  | 'onChange'
  | 'onBlur'
  | 'onFocus'
  | 'name'
  | 'id'
  | 'type'
  | 'placeholder'
  | 'label'
  | 'helperText'
  | 'error'
  | 'required'
  | 'disabled'
  | 'autoFocus'
  | 'autoComplete'
  | 'fullWidth'
  | 'multiline'
  | 'rows'
  | 'minRows'
  | 'maxRows'
  | 'size'
  | 'variant'
  | 'inputRef'
  | 'className'
  | 'style'
  | 'title'
  | 'aria-label'
> {
  /** Key and click handlers of the control itself (MUI types them for its root div). */
  onKeyDown?: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onClick?: MouseEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  /** Attributes of the native `<input>` / `<textarea>` (MUI's `slotProps.htmlInput`). */
  inputProps?: InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>;
  /** Shown inside the field before the text, e.g. a currency symbol. */
  startAdornment?: ReactNode;
  /** Shown inside the field after the text, e.g. a unit or a small icon button. */
  endAdornment?: ReactNode;
  /** The root element (MUI's `ref`); the input itself is `inputRef`. */
  ref?: Ref<HTMLDivElement>;
}

export function TextField({
  label,
  helperText,
  error,
  required,
  fullWidth,
  multiline,
  rows,
  minRows,
  maxRows,
  size,
  variant,
  inputRef,
  inputProps,
  startAdornment,
  endAdornment,
  className,
  style,
  title,
  id: idProp,
  'aria-label': ariaLabel,
  onKeyDown,
  onClick,
  ref,
  ...input
}: TextFieldProps) {
  const { useV2Components } = useFlags<MaggieFeatureFlags>();
  const generatedId = useId();

  if (!useV2Components) {
    return (
      <MuiTextField
        ref={ref}
        id={idProp}
        label={label}
        helperText={helperText}
        error={error}
        required={required}
        fullWidth={fullWidth}
        multiline={multiline}
        rows={rows}
        minRows={minRows}
        maxRows={maxRows}
        size={size}
        variant={variant}
        inputRef={inputRef}
        className={className}
        style={style}
        title={title}
        // MUI attaches these to its root div; the events bubble up from the input.
        onKeyDown={onKeyDown as MuiTextFieldProps['onKeyDown']}
        onClick={onClick as MuiTextFieldProps['onClick']}
        slotProps={{
          htmlInput: { 'aria-label': ariaLabel, ...inputProps },
          input: {
            startAdornment: startAdornment && (
              <InputAdornment position='start'>{startAdornment}</InputAdornment>
            ),
            endAdornment: endAdornment && <InputAdornment position='end'>{endAdornment}</InputAdornment>,
          },
        }}
        {...input}
      />
    );
  }

  const id = idProp ?? inputProps?.id ?? generatedId;
  const helperId = helperText ? `${id}-helper` : undefined;
  // MUI types `value` as unknown; the native controls take what the app passes (strings, numbers).
  const { value, defaultValue, ...nativeInput } = input;
  const controlProps = {
    ref: inputRef,
    required,
    onKeyDown,
    onClick,
    value: value as InputHTMLAttributes<HTMLInputElement>['value'],
    defaultValue: defaultValue as InputHTMLAttributes<HTMLInputElement>['defaultValue'],
    ...nativeInput,
    ...inputProps,
    // The label and helper-text wiring wins over `inputProps` (MUI lets `inputProps` break it).
    id,
    'aria-label': ariaLabel ?? inputProps?.['aria-label'],
    'aria-invalid': error || inputProps?.['aria-invalid'] || undefined,
    'aria-describedby': [inputProps?.['aria-describedby'], helperId].filter(Boolean).join(' ') || undefined,
  };
  const grouped = Boolean(startAdornment || endAdornment);

  // MUI's multiline grows with its content from `minRows` (1) up to `maxRows`; `rows` fixes it.
  const textareaProps = {
    ...controlProps,
    rows: Number(rows ?? minRows ?? 1),
    className: cn('min-h-0 resize-none', rows ? 'field-sizing-fixed' : 'field-sizing-content'),
    style: maxRows && !rows ? { maxHeight: `calc(${maxRows}lh + 0.75rem)` } : undefined,
  };

  let control: ReactNode;
  if (grouped) {
    control = (
      <InputGroup>
        {startAdornment && (
          <InputGroupAddon>
            <InputGroupText>{startAdornment}</InputGroupText>
          </InputGroupAddon>
        )}
        {multiline ? <InputGroupTextarea {...textareaProps} /> : <InputGroupInput {...controlProps} />}
        {endAdornment && (
          <InputGroupAddon align='inline-end'>
            <InputGroupText>{endAdornment}</InputGroupText>
          </InputGroupAddon>
        )}
      </InputGroup>
    );
  } else if (multiline) {
    control = <V2Textarea {...textareaProps} />;
  } else {
    control = <V2Input {...controlProps} />;
  }

  return (
    <div
      ref={ref}
      data-slot='text-field'
      className={cn('inline-flex flex-col gap-1 align-top', fullWidth && 'flex w-full', className)}
      style={style as CSSProperties | undefined}
      title={title}
    >
      {label && (
        <label htmlFor={id} className='text-xs text-muted-foreground'>
          {label}
          {required && <span aria-hidden> *</span>}
        </label>
      )}
      {control}
      {helperText && (
        <p id={helperId} className={cn('m-0 text-xs', error ? 'text-destructive' : 'text-muted-foreground')}>
          {helperText}
        </p>
      )}
    </div>
  );
}
